import {createDomElementHandler} from "@/scripts/utils/dom.utils";
import {normalizeToNumber} from "@shared/utils/numbers.utils";

export class AbStarsController {

  private _rawVal = 0;
  private _selectable = false;
  private _disabled = false;
  private _eventsBound = false;
  private _size: string;

  constructor(private rootEl: HTMLElement) {
  }

  get value() {
    return Math.round(this._rawVal * 2) / 2;
  }

  public syncFromAttributes() {
    const attrValue = this.rootEl.getAttribute("rating");
    this._size = this.rootEl.getAttribute("size");
    const normalized = normalizeToNumber(attrValue);
    this._rawVal = normalized <= 5 && normalized > 0 ? normalized : 0;
    this._disabled = this.rootEl.hasAttribute("disabled");
    this._selectable = !this._disabled && this.rootEl.hasAttribute("selectable");
  }

  public render() {
    this.rootEl.classList.add("ab-stars");

    const rootRole = this._selectable ? "radiogroup" : "img";

    const rootAriaLabel = this._selectable ?
      "Seleziona una valutazione da 0 a 5" :
      `Valutazione ${this._rawVal} su 5`;

    const rootTabIndex = this._selectable ? 0 : -1;

    this.rootEl.setAttribute("role", rootRole);
    this.rootEl.setAttribute("aria-label", rootAriaLabel);
    this.rootEl.tabIndex = rootTabIndex;

    if( this._disabled ) {
      this.rootEl.setAttribute("aria-disabled", "true");
      this.rootEl.classList.add("ab-stars--disabled");
    }

    if( this._size?.length ) {
      if(['lg','xl','xxl'].includes(this._size)) {
        this.rootEl.classList.add(`ab-stars--size-${this._size}`);
      }
    }

    if( this._selectable ) {
      this.rootEl.classList.add("ab-stars--selectable");
    }

    const fragment = document.createDocumentFragment();
    const container = createDomElementHandler("ul", "ab-stars__container");
    fragment.appendChild(container);

    for (let i = 1; i <= 5; i++) {
      let iconClass = "icon-star-empty";
      if (i <= this.value) iconClass = "icon-star";
      else if (i - 0.5 === this.value) iconClass = "icon-star-half";
      const classList = ["ab-stars__single"];
      const li = createDomElementHandler("li", classList);
      li.append(createDomElementHandler("span", iconClass));
      if (this._selectable) {
        li.setAttribute("role", "radio");
        li.setAttribute("aria-checked", String(i === this.value));
        li.dataset.index = String(i);
      }
      container.appendChild(li);
    }

    this.rootEl.replaceChildren(fragment);
  }

  public bindEvents() {
    if (this._eventsBound) return;
    this._eventsBound = true;

    this.rootEl.addEventListener("click", (e) => {
      if (!this._selectable) return;
      const target = e.target as HTMLElement | null;
      const li = target?.closest?.(".ab-stars__single") as HTMLElement | null;
      if (!li) return;
      const index = parseInt(li.dataset.index ?? "0", 10) || 0;
      if (index > 0) {
        this._emitChange(index);
      }
    });

    this.rootEl.addEventListener("keydown", (e: KeyboardEvent) => {
      if (!this._selectable) return;
      let newValue;
      const key = e.key;
      if (["ArrowLeft", "ArrowDown"].includes(key)) {
        newValue = Math.max(0, this.value - 1);
      } else if (["ArrowRight", "ArrowUp"].includes(key)) {
        newValue = Math.min(5, this.value + 1);
      } else if (key === "Home") {
        newValue = 0;
      } else if (key === "End") {
        newValue = 5;
      }
      if (newValue !== undefined) {
        e.preventDefault();
        this._emitChange(newValue);
      }
    });
  }

  private _emitChange(value: number) {
    this.rootEl.setAttribute("rating", String(value));
    this.rootEl.dispatchEvent(new CustomEvent("change", {
      detail: {value},
      bubbles: true, composed: true
    }));
  }

}